/**
 * ユーザーWeb向け管理お知らせの送信。
 * - 全体お知らせ（targetType: all）
 * - 個別デバイスへのお知らせ（targetType: device）
 * タブレット向け oshirase_carecenter とは別コレクション。
 * 削除は論理削除（deletedAt）。ユーザーWebは差分同期で削除を反映する。
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import type { Device } from "./web_parts/types";

const COLLECTION = "admin_web_notices";
const HISTORY_LIMIT = 20;

export type UserWebNoticeTarget = "all" | "device";

export interface InfoSend_UserWebNoticeProps {
  /** device: 単一デバイス向け。all: 管理者全体向け */
  target: UserWebNoticeTarget;
  /** target=device のとき必須 */
  device?: Device | null;
  /** モーダル表示時に閉じるコールバック（デバイス操作から呼ばれた場合） */
  onClose?: () => void;
  /** true のとき自身で Modal を出さずインライン表示（共通設定タブ用） */
  inline?: boolean;
}

type NoticeRow = {
  id: string;
  title: string;
  body: string;
  createdAt: number;
  createdBy: string;
  targetType: string;
  deviceIds: string[];
};

const InfoSend_UserWebNotice: React.FC<InfoSend_UserWebNoticeProps> = ({
  target,
  device,
  onClose,
  inline = false,
}) => {
  const { displayName, currentUser } = useAuth();
  const [form] = Form.useForm<{ title: string; body: string }>();
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<NoticeRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(!inline && !!onClose);

  const isDeviceTarget = target === "device";
  const deviceId = device?.id || device?.serialNumber || "";

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      // 複合インデックス不要（作成直後の構築待ちを避ける）。createdAt のみで取得しクライアントで絞る。
      const q = query(
        collection(db, COLLECTION),
        orderBy("createdAt", "desc"),
        limit(80)
      );
      const snap = await getDocs(q);
      const rows: NoticeRow[] = [];
      const wantType = isDeviceTarget ? "device" : "all";
      for (const d of snap.docs) {
        const data = d.data();
        // 論理削除済みは履歴に出さない
        if (typeof data.deletedAt === "number" && data.deletedAt > 0) continue;
        const targetType =
          data.targetType === "device" ? "device" : data.targetType === "all" ? "all" : "";
        if (targetType !== wantType) continue;
        const deviceIds = Array.isArray(data.deviceIds)
          ? data.deviceIds.filter((v): v is string => typeof v === "string")
          : [];
        if (isDeviceTarget && deviceId && !deviceIds.includes(deviceId)) continue;
        rows.push({
          id: d.id,
          title: typeof data.title === "string" ? data.title : "",
          body: typeof data.body === "string" ? data.body : "",
          createdAt: typeof data.createdAt === "number" ? data.createdAt : 0,
          createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
          targetType,
          deviceIds,
        });
        if (rows.length >= HISTORY_LIMIT) break;
      }
      setHistory(rows);
    } catch (e) {
      console.error(e);
      message.error("送信履歴の取得に失敗しました");
    } finally {
      setLoadingHistory(false);
    }
  }, [isDeviceTarget, deviceId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleSend = async () => {
    try {
      const values = await form.validateFields();
      if (isDeviceTarget && !deviceId) {
        message.error("デバイスが選択されていません");
        return;
      }
      setSending(true);
      const createdBy =
        displayName?.trim() ||
        currentUser?.email?.trim() ||
        "admin";
      const now = Date.now();
      await addDoc(collection(db, COLLECTION), {
        targetType: isDeviceTarget ? "device" : "all",
        deviceIds: isDeviceTarget ? [deviceId] : [],
        title: values.title.trim(),
        body: values.body.trim(),
        createdAt: now,
        createdBy,
      });
      message.success(
        isDeviceTarget
          ? "このデバイスへのお知らせを送信しました"
          : "全体お知らせを送信しました"
      );
      form.resetFields();
      await loadHistory();
      if (onClose) {
        setModalOpen(false);
        onClose();
      }
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      console.error(e);
      message.error("お知らせの送信に失敗しました");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (row: NoticeRow) => {
    setDeletingId(row.id);
    try {
      await updateDoc(doc(db, COLLECTION, row.id), {
        deletedAt: Date.now(),
      });
      message.success("お知らせを削除しました");
      await loadHistory();
    } catch (e) {
      console.error(e);
      message.error("お知らせの削除に失敗しました");
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnsType<NoticeRow> = [
    {
      title: "送信日時",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (v: number) =>
        v
          ? format(new Date(v), "yyyy/MM/dd HH:mm", { locale: ja })
          : "—",
    },
    {
      title: "タイトル",
      dataIndex: "title",
      key: "title",
      width: 180,
      ellipsis: true,
    },
    {
      title: "本文",
      dataIndex: "body",
      key: "body",
      ellipsis: true,
    },
    {
      title: "送信者",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 120,
      ellipsis: true,
    },
    {
      title: "操作",
      key: "action",
      width: 80,
      render: (_, row) => (
        <Popconfirm
          title="このお知らせを削除しますか？"
          description="ユーザーWebからも配信対象外になります（論理削除）。"
          onConfirm={() => void handleDelete(row)}
          okText="削除"
          cancelText="キャンセル"
          okType="danger"
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={deletingId === row.id}
            aria-label="お知らせを削除"
          />
        </Popconfirm>
      ),
    },
  ];

  const formBlock = (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          isDeviceTarget
            ? "選択中デバイスの管理者ユーザーの Web に「このデバイスへのお知らせ」として表示されます。"
            : "すべてのデバイス管理者ユーザーの Web に「全体お知らせ」として表示されます（親族には表示されません）。"
        }
      />
      {isDeviceTarget && (
        <Typography.Paragraph style={{ marginBottom: 12 }}>
          対象デバイス:{" "}
          <strong>
            {device?.deviceName || device?.serialNumber || deviceId || "—"}
          </strong>
        </Typography.Paragraph>
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="タイトル"
          rules={[
            { required: true, message: "タイトルを入力してください" },
            { max: 80, message: "80文字以内で入力してください" },
          ]}
        >
          <Input placeholder="お知らせのタイトル" maxLength={80} />
        </Form.Item>
        <Form.Item
          name="body"
          label="本文"
          rules={[
            { required: true, message: "本文を入力してください" },
            { max: 1000, message: "1000文字以内で入力してください" },
          ]}
        >
          <Input.TextArea
            rows={4}
            autoSize={{ minRows: 4, maxRows: 10 }}
            placeholder="お知らせの本文"
            maxLength={1000}
            showCount
            style={{ minHeight: 96 }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" loading={sending} onClick={() => void handleSend()}>
              送信
            </Button>
            {onClose && (
              <Button
                onClick={() => {
                  setModalOpen(false);
                  onClose();
                }}
              >
                閉じる
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
      <Typography.Title level={5} style={{ marginTop: 8 }}>
        送信履歴（直近）
      </Typography.Title>
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={history}
        loading={loadingHistory}
        pagination={false}
        scroll={{ x: 680 }}
      />
    </>
  );

  if (inline) {
    return <div>{formBlock}</div>;
  }

  return (
    <Modal
      title={
        isDeviceTarget
          ? "ユーザーWebへのお知らせ（このデバイス）"
          : "ユーザーWeb 全体お知らせ"
      }
      open={modalOpen}
      onCancel={() => {
        setModalOpen(false);
        onClose?.();
      }}
      footer={null}
      width={720}
      destroyOnClose
    >
      {formBlock}
    </Modal>
  );
};

export default InfoSend_UserWebNotice;
